// Figma: node 18034:1411 ("Resident Action Feed" table).
// LOGIC PORTED from LC2's FeedComponent.js (commons/components/HomeTabs/Today/Feed/):
// same `coreApiSlice.getAccountFeed` query, same community/campaign/permission filtering
// of `data.rows`. Visuals rebuilt on DataTableShell instead of MUI DataGrid Pro (see that
// file's header comment for why). Not ported yet: the "Flag" (EditRenterAction) and
// "Confirm" (ConfirmActionButton) action columns — those open modals tied to reducers/
// components not in scope for this pass.
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {Box, Typography} from '@mui/material';

import {useGetAccountFeedQuery} from '../../features/coreApi/apiSlice';
import {getCampaigns, getCommunities} from '../../app/store/reducers/entities';
import {getIsStakeAdmin, getPropertyPermissions, getTeamMemberRole} from '../../app/store/reducers/session';
import DataTableShell from './components/DataTableShell';

const formatCurrency = (value) =>
    typeof value === 'number' ? `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : value;

const ResidentActionFeedTable = () => {
    const params = useParams();
    const accountSfid = params?.account_sfid;
    const communitySfid = params?.community_sfid;
    const campaignSfid = params?.campaign_sfid;

    const campaigns = useSelector(getCampaigns);
    const communities = useSelector(getCommunities);
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const teamMemberRole = useSelector(getTeamMemberRole);
    const isAdministrator = teamMemberRole === 'Administrator';
    const propertyPermissions = useSelector(getPropertyPermissions);

    const {data, isLoading, isFetching, isError} = useGetAccountFeedQuery(accountSfid, {skip: !accountSfid});

    const rows = useMemo(() => {
        if (!data?.rows) return [];

        if (campaignSfid) {
            const campaign = campaigns.find((c) => c.sfid === campaignSfid);
            return data.rows.filter((row) => row.campaign === campaign?.name);
        }
        if (communitySfid) {
            const community = communities.find((c) => c.sfid === communitySfid);
            return data.rows.filter((row) => row.property === community?.name);
        }
        if (!isStakeAdmin && !isAdministrator) {
            const permissionCommunities = communities.filter((c) => propertyPermissions.includes(c.sfid));
            const names = permissionCommunities.map((c) => c.name);
            return data.rows.filter((row) => names.includes(row.property));
        }
        return data.rows;
    }, [data, campaignSfid, communitySfid, communities, campaigns, isStakeAdmin, isAdministrator, propertyPermissions]);

    const columns = useMemo(
        () => [
            {
                field: 'renter_action',
                headerName: 'Action',
                width: 280,
                renderCell: (row) => <Typography variant="body2" dangerouslySetInnerHTML={{__html: row.renter_action}} />
            },
            {field: 'unit_number', headerName: 'Unit Number', width: 140},
            {field: 'floor_plan', headerName: 'Floor Plan', width: 120},
            {field: 'property', headerName: 'Community', width: 180},
            {field: 'campaign', headerName: 'Campaign', width: 170},
            {field: 'rent_amount', headerName: 'In Place Rent', width: 140, renderCell: (row) => formatCurrency(row.rent_amount)},
            {field: 'market_rent', headerName: 'Market Rent', width: 140, renderCell: (row) => formatCurrency(row.market_rent)},
            {field: 'rent_delta', headerName: 'Delta', width: 150}
        ],
        []
    );

    if (isLoading || isFetching) {
        return (
            <Box sx={{p: 4, textAlign: 'center'}}>
                <Typography variant="body2" color="text.secondary">
                    Loading resident actions…
                </Typography>
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{p: 4, textAlign: 'center'}}>
                <Typography variant="body2" color="error.main">
                    Error fetching recent resident updates.
                </Typography>
            </Box>
        );
    }

    return (
        <DataTableShell
            title="Resident Action Feed"
            description="See recent actions your Stakers have taken"
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            searchPlaceholder="Search Resident Actions"
            filterable
        />
    );
};

export default ResidentActionFeedTable;
